import React, { useCallback, useRef, useState } from "react";
import Spinner from "../../../components/Spinner/Spinner.tsx";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import { generatePDFDocumentFromRef } from "../../../util/generatePDF.tsx";
import { submitContact } from "../../../api/contact";

interface MortgageInputs {
  type: "interestOnly" | "repayment";
  price: number | null;
  deposit: number | null;
  term: number;
  rate: number;
}

interface YearlyItem {
  year: number;
  remainingDebt: string;
  interestPaid: string;
  capitalRepaid: string;
  totalPayment: string;
}

interface MortgageResultData {
  monthlyPayment: string;
  totalRepay: string;
  totalInterest: string;
  totalPrincipal: string;
  yearlyBreakdown: YearlyItem[];
}

interface MortgageResult {
  status: "null" | "success";
  data: MortgageResultData | null;
}

interface MortgagePage2Props {}

export default function MortgagePage2(props: MortgagePage2Props): JSX.Element {
  const location = useLocation();
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactMessage, setContactMessage] = useState("");
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactError, setContactError] = useState<string | null>(null);
  const [contactSuccess, setContactSuccess] = useState(false);

  const [inputs, setInputs] = useState<MortgageInputs>({
    type: "interestOnly",
    price: 450000,
    deposit: 112500,
    term: 25,
    rate: 4.5,
  });
  const [result, setResult] = useState<MortgageResult>({
    status: "null",
    data: null,
  });
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState<boolean>(false);
  const tableRef = useRef<HTMLDivElement>(null);

  const invalidateResults = useCallback(() => {
    setResult({ status: "null", data: null });
    setError("");
  }, []);

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setContactError(null);
    setContactSuccess(false);
    setContactSubmitting(true);

    try {
      await submitContact({
        name: contactName.trim(),
        email: contactEmail.trim(),
        phone: contactPhone.trim() || undefined,
        subject: "Mortgage Broker Enquiry",
        message: contactMessage.trim(),
        sourcePage: location.pathname,
        enquiryType: "general",
      });

      setContactSuccess(true);
      setContactName("");
      setContactEmail("");
      setContactPhone("");
      setContactMessage("");
    } catch (err) {
      setContactError(
        err instanceof Error ? err.message : "Failed to send your enquiry"
      );
    } finally {
      setContactSubmitting(false);
    }
  };

  const ltvPercent = (() => {
    if (!inputs.price || !inputs.deposit || inputs.price <= 0) return 0;
    const pct = ((inputs.price - inputs.deposit) / inputs.price) * 100;
    return Math.max(0, Math.min(100, Math.round(pct)));
  })();

  function calculateMortgage(
    mortgageType: "interestOnly" | "repayment",
    propertyPrice: number,
    deposit: number,
    termYears: number,
    annualInterestRate: number
  ): MortgageResultData | string {
    const loanAmount = propertyPrice - deposit;
    const monthlyInterestRate = annualInterestRate / 12 / 100;
    const totalPayments = termYears * 12;
    let monthlyPayment: number;
    let totalRepay: number = 0;
    let totalInterest: number = 0;
    let totalPrincipal: number = 0;
    let yearlyBreakdown: YearlyItem[] = [];

    if (mortgageType === "repayment") {
      monthlyPayment =
        (loanAmount * monthlyInterestRate) /
        (1 - Math.pow(1 + monthlyInterestRate, -totalPayments));
      let remainingDebt = loanAmount;
      let yearInterest: number = 0;
      let yearPrincipal: number = 0;
      for (let month = 1; month <= totalPayments; month++) {
        if (remainingDebt > 0) {
          let interestForThisMonth = remainingDebt * monthlyInterestRate;
          let principalForThisMonth = monthlyPayment - interestForThisMonth;
          if (remainingDebt - principalForThisMonth < 0) {
            principalForThisMonth = remainingDebt;
          }
          remainingDebt -= principalForThisMonth;
          // Make sure the remainingDebt does not go negative
          if (remainingDebt < 0) remainingDebt = 0;
          yearInterest += interestForThisMonth;
          yearPrincipal += principalForThisMonth;
          totalInterest += interestForThisMonth;
          totalPrincipal += principalForThisMonth;
        }
        // Use correct breakdown: push every 12th month or last payment
        if (month % 12 === 0 || month === totalPayments) {
          yearlyBreakdown.push({
            year: Math.ceil(month / 12),
            remainingDebt: (remainingDebt > 0 ? remainingDebt : 0).toFixed(2),
            interestPaid: yearInterest.toFixed(2),
            capitalRepaid: yearPrincipal.toFixed(2),
            totalPayment: (yearInterest + yearPrincipal).toFixed(2),
          });
          yearInterest = 0;
          yearPrincipal = 0;
        }
      }
      totalRepay = totalInterest + totalPrincipal;
    } else if (mortgageType === "interestOnly") {
      monthlyPayment = loanAmount * monthlyInterestRate;
      for (let year = 1; year <= termYears; year++) {
        yearlyBreakdown.push({
          year: year,
          remainingDebt: loanAmount.toFixed(2),
          interestPaid: (monthlyPayment * 12).toFixed(2),
          capitalRepaid: (year === termYears ? loanAmount : 0).toFixed(2),
          totalPayment: (year < termYears
            ? monthlyPayment * 12
            : monthlyPayment * 12 + loanAmount
          ).toFixed(2),
        });
      }
      totalPrincipal = loanAmount;
      totalInterest = monthlyPayment * totalPayments;
      totalRepay = totalInterest + totalPrincipal;
    } else {
      return "Invalid mortgage type";
    }

    return {
      monthlyPayment: monthlyPayment.toFixed(2),
      totalRepay: totalRepay.toFixed(2),
      totalInterest: totalInterest.toFixed(2),
      totalPrincipal: totalPrincipal.toFixed(2),
      yearlyBreakdown,
    };
  }

  async function handleCalculateMortgage(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    const { type, price, deposit, term, rate } = inputs;
    if (
      price === null ||
      deposit === null ||
      isNaN(price) ||
      isNaN(deposit) ||
      !term ||
      !rate
    ) {
      setError("Please complete all fields.");
      setLoading(false);
      return;
    }
    const calculationResult = calculateMortgage(
      type,
      price,
      deposit as number,
      term as number,
      rate
    );

    if (typeof calculationResult === "string") {
      setError(calculationResult);
      setResult({ status: "null", data: null });
      setLoading(false);
    } else if (calculationResult) {
      setResult({
        status: "success",
        data: calculationResult,
      });
      setLoading(false);
    }
  }

  function handleDownloadPDF() {
    setIsGeneratingPDF(true);
    setTimeout(async () => {
      if (tableRef.current) {
        try {
          const doc = await generatePDFDocumentFromRef(tableRef);
          if (doc) {
            doc.save(`Mortgage Calculation.pdf`);
          }
        } catch (err) {
          // Ignore
        } finally {
          setIsGeneratingPDF(false);
        }
      }
    }, 0);
  }

  // --- Hide input type=number arrows using custom style:
  // This places a style tag. If your project already styles .hideSpinner, you may remove/rework this.
  React.useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      input[type=number].hideSpinner::-webkit-inner-spin-button,
      input[type=number].hideSpinner::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
      }
      input[type=number].hideSpinner {
          -moz-appearance: textfield;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Mortgage Calculator | Letting Engine</title>
        <meta
          name="description"
          content="Calculate your monthly repayments for Buy-to-Let or Residential mortgages. Adjust leverage, interest rates, and terms to stress-test your investment."
        />
        <meta
          name="keywords"
          content="residential property, real estate, london, mortgage calculator, buy-to-let, repayment, interest-only"
        />
      </Helmet>
      {/* HEADER */}
      <section className="page-header py-32 pb-16 max-w-[1600px] mx-auto px-[5%]">
        <h1 className="font-teko text-7xl tracking-[-1px] mb-4">
          MORTGAGE
          <br />
          <span className="text-brand-yellow">ENGINE</span>
        </h1>
        <p className="font-manrope text-lg text-text-slate max-w-[700px] leading-relaxed">
          Calculate your monthly repayments for Buy-to-Let or Residential
          mortgages. Adjust leverage, interest rates, and terms to stress-test
          your investment.
        </p>
      </section>

      {/* MAIN LAYOUT */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] lg:gap-10 max-w-[1600px] mx-auto px-[5%] pb-32 ">
        {/* LEFT: CALCULATOR */}
        <div className="col-span-2 lg:col-span-1">
          <div
            className="bg-bg-white border border-solid border-[rgba(0,0,0,0.05)] p-6 sm:p-16 relative overflow-hidden clip-path-[polygon(0_0,100%_0,100%_95%,95%_100%,0_100%)] shadow-soft"
            ref={tableRef}
          >
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-0 justify-between items-start sm:items-end mb-12 border-b border-b-solid border-b-[rgba(0,0,0,0.05)] pb-4">
              <h3 className="font-teko text-3xl text-text-obsidian">
                Loan Parameters
              </h3>
              <div className="flex bg-bg-porcelain border border-solid border-[#ddd] p-1.5 transform ">
                <button
                  className={`py-2 px-6 font-teko text-lg uppercase cursor-pointer transition-all duration-300 transform  ${
                    inputs.type === "interestOnly"
                      ? "bg-text-obsidian text-brand-yellow"
                      : "text-text-slate"
                  }`}
                  onClick={() => {
                    invalidateResults();
                    setInputs({ ...inputs, type: "interestOnly" });
                  }}
                  type="button"
                >
                  Interest Only
                </button>
                <button
                  className={`py-2 px-6 font-teko text-lg uppercase cursor-pointer transition-all duration-300 transform ${
                    inputs.type === "repayment"
                      ? "bg-text-obsidian text-brand-yellow"
                      : "text-text-slate"
                  }`}
                  onClick={() => {
                    invalidateResults();
                    setInputs({ ...inputs, type: "repayment" });
                  }}
                  type="button"
                >
                  Repayment
                </button>
              </div>
            </div>

            <form className="grid gap-10" onSubmit={handleCalculateMortgage}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 ">
                {/* Property Value */}
                <div className="">
                  <label
                    htmlFor="propertyValue"
                    className="flex justify-between font-teko text-lg mb-3 uppercase text-text-obsidian tracking-wide"
                  >
                    Property Value
                  </label>
                  <div className="flex items-center border-b-2 border-b-solid border-b-[#ddd] transition-all duration-300 bg-bg-porcelain focus-within:border-brand-yellow focus-within:bg-white">
                    <span className="font-manrope text-lg text-text-slate pl-4">
                      £
                    </span>
                    <input
                      type="number"
                      min={0}
                      className="w-full border-none p-4 text-lg font-manrope text-text-obsidian outline-none bg-transparent hideSpinner"
                      // The 'hideSpinner' class is sufficient to hide arrows as defined above
                      value={inputs.price ?? ""}
                      onChange={(e) => {
                        invalidateResults();
                        setInputs({
                          ...inputs,
                          price: Number(e.target.value) || 0,
                        });
                      }}
                      id="propertyValue"
                    />
                  </div>
                </div>
                {/* Deposit */}
                <div className="">
                  <label
                    htmlFor="deposit"
                    className="flex justify-between font-teko text-lg mb-3 uppercase text-text-obsidian tracking-wide"
                  >
                    Deposit{" "}
                    <span className="font-manrope text-sm text-text-slate normal-case tracking-normal">
                      (
                      {(
                        ((inputs.deposit || 0) / (inputs.price || 1)) *
                        100
                      ).toFixed(0)}
                      %)
                    </span>
                  </label>
                  <div className="flex items-center border-b-2 border-b-solid border-b-[#ddd] transition-all duration-300 bg-bg-porcelain focus-within:border-brand-yellow focus-within:bg-white">
                    <span className="font-manrope text-lg text-text-slate pl-4">
                      £
                    </span>
                    <input
                      type="number"
                      min={0}
                      max={inputs.price || undefined}
                      className="w-full border-none p-4 text-lg font-manrope text-text-obsidian outline-none bg-transparent hideSpinner"
                      value={inputs.deposit ?? ""}
                      onChange={(e) => {
                        invalidateResults();
                        setInputs({
                          ...inputs,
                          deposit: Number(e.target.value) || 0,
                        });
                      }}
                      id="deposit"
                    />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 ">
                {/* Interest Rate */}
                <div className="">
                  <label
                    htmlFor="interestRate"
                    className="flex justify-between font-teko text-lg mb-3 uppercase text-text-obsidian tracking-wide"
                  >
                    Interest Rate{" "}
                    <span className="font-manrope text-sm text-text-slate normal-case tracking-normal">
                      (%)
                    </span>
                  </label>
                  <div className="flex items-center border-b-2 border-b-solid border-b-[#ddd] transition-all duration-300 bg-bg-porcelain focus-within:border-brand-yellow focus-within:bg-white">
                    <span className="font-manrope text-lg text-text-slate pl-4">
                      %
                    </span>
                    <input
                      type="number"
                      min={0}
                      step={0.01}
                      className="w-full border-none p-4 text-lg font-manrope text-text-obsidian outline-none bg-transparent hideSpinner"
                      value={inputs.rate}
                      onChange={(e) => {
                        invalidateResults();
                        setInputs({ ...inputs, rate: Number(e.target.value) });
                      }}
                      id="interestRate"
                    />
                  </div>
                </div>
                {/* Term */}
                <div className="">
                  <label
                    htmlFor="term"
                    className="flex justify-between font-teko text-lg mb-3 uppercase text-text-obsidian tracking-wide"
                  >
                    Term{" "}
                    <span className="font-manrope text-sm text-text-slate normal-case tracking-normal">
                      (Years)
                    </span>
                  </label>
                  <div className="flex items-center border-b-2 border-b-solid border-b-[#ddd] transition-all duration-300 bg-bg-porcelain focus-within:border-brand-yellow focus-within:bg-white">
                    <span className="font-manrope text-lg text-text-slate pl-4">
                      Yr
                    </span>
                    <input
                      type="number"
                      min={1}
                      max={50}
                      className="w-full border-none p-4 text-lg font-manrope text-text-obsidian outline-none bg-transparent hideSpinner"
                      value={inputs.term}
                      onChange={(e) => {
                        invalidateResults();
                        setInputs({ ...inputs, term: Number(e.target.value) });
                      }}
                      id="term"
                    />
                  </div>
                </div>
              </div>
              <button
                type="submit"
                className="w-full p-6 bg-brand-yellow mt-8 border-none text-text-obsidian font-teko text-2xl uppercase tracking-wide cursor-pointer transition-all duration-300 clip-path-[polygon(0_0,100%_0,100%_85%,97%_100%,0_100%)] hover:translate-y-[-3px] hover:shadow-[0_15px_30px_rgba(255,196,0,0.2)]"
              >
                {loading ? <Spinner /> : "Calculate Mortgage"}
              </button>
            </form>

            {result.status === "success" && result.data && (
              <div className="mt-16 bg-bg-white text-text-obsidian p-12 relative border border-solid border-[rgba(0,0,0,0.06)] shadow-sm">
                <div className="flex justify-between items-end mb-4">
                  <span className="font-manrope uppercase tracking-wider text-sm text-text-slate m-0">
                    Loan to Value
                  </span>
                  <span className="font-manrope uppercase tracking-wider text-sm text-brand-yellow font-bold m-0">
                    {inputs.price && inputs.deposit && inputs.price > 0
                      ? (
                          ((inputs.price - inputs.deposit) / inputs.price) *
                          100
                        ).toFixed(0)
                      : 0}
                    %
                  </span>
                </div>
                <div className="w-full h-2 bg-[rgba(0,0,0,0.08)] my-8 relative ltv-bar-container">
                  <svg
                    className="absolute inset-0 w-full h-full text-brand-yellow"
                    viewBox="0 0 100 10"
                    preserveAspectRatio="none"
                    aria-hidden="true"
                  >
                    <rect
                      x="0"
                      y="0"
                      width="100"
                      height="10"
                      fill="rgba(0,0,0,0.08)"
                    />
                    <rect
                      x="0"
                      y="0"
                      width={ltvPercent}
                      height="10"
                      fill="currentColor"
                      rx="1"
                      ry="1"
                    />
                    {/* <rect
                      x={Math.max(0, Math.min(100, ltvPercent)) - 0.6}
                      y="0"
                      width="1.2"
                      height="10"
                      fill="rgba(0,0,0,0.25)"
                    /> */}
                  </svg>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 border-t border-t-solid border-t-[rgba(0,0,0,0.09)] pt-8  mb-5">
                  <div className="relative">
                    <label className="block font-manrope uppercase tracking-wider text-xs text-[#888] mb-1">
                      Monthly Payment
                    </label>
                    <div className="font-teko text-5xl text-brand-yellow leading-none">
                      £{Number(result.data.monthlyPayment).toLocaleString()}
                    </div>
                  </div>
                  <div className="relative">
                    <label className="block font-manrope uppercase tracking-wider text-xs text-[#888] mb-1">
                      Total Loan Amount
                    </label>
                    <div className="font-teko text-5xl text-text-obsidian leading-none">
                      £
                      {inputs.price && inputs.deposit
                        ? (inputs.price - inputs.deposit).toLocaleString()
                        : "0"}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 border-t border-t-solid border-t-[rgba(0,0,0,0.09)] pt-8">
                  <div className="relative">
                    <label className="block font-manrope uppercase tracking-wider text-xs text-[#888] mb-1">
                      Total Interest
                    </label>
                    <div className="font-teko text-3xl text-brand-yellow leading-none">
                      £{Number(result.data.totalInterest).toLocaleString()}
                    </div>
                  </div>
                  <div className="relative">
                    <label className="block font-manrope uppercase tracking-wider text-xs text-[#888] mb-1">
                      Total Repayment
                    </label>
                    <div className="font-teko text-3xl text-text-obsidian leading-none">
                      £{Number(result.data.totalRepay).toLocaleString()}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 border-t border-t-solid border-t-[rgba(0,0,0,0.09)] pt-8 mt-5">
                  <div className="overflow-auto">
                    <table className="w-full border-collapse mt-2 text-xs text-left font-manrope bg-white">
                      <thead>
                        <tr>
                          <th className="py-2 px-2 font-bold">Year</th>
                          <th className="py-2 px-2 font-bold">
                            Remaining Debt
                          </th>
                          <th className="py-2 px-2 font-bold">Interest Paid</th>
                          <th className="py-2 px-2 font-bold">
                            Capital Repaid
                          </th>
                          <th className="py-2 px-2 font-bold">Total Payment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.data.yearlyBreakdown.map((item, idx) => (
                          <tr
                            key={item.year}
                            className={
                              idx % 2 === 0 ? "bg-bg-porcelain" : "bg-bg-white"
                            }
                          >
                            <td className="py-2 px-2">{item.year}</td>
                            <td className="py-2 px-2">
                              £{Number(item.remainingDebt).toLocaleString()}
                            </td>
                            <td className="py-2 px-2">
                              £{Number(item.interestPaid).toLocaleString()}
                            </td>
                            <td className="py-2 px-2">
                              £{Number(item.capitalRepaid).toLocaleString()}
                            </td>
                            <td className="py-2 px-2">
                              £{Number(item.totalPayment).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <button
                  className="w-full p-4 mt-8 bg-brand-yellow text-text-obsidian font-teko text-xl uppercase tracking-wide cursor-pointer transition-all duration-300 clip-path-[polygon(0_0,100%_0,100%_85%,97%_100%,0_100%)] hover:translate-y-[-3px] hover:shadow-[0_15px_30px_rgba(255,196,0,0.2)]"
                  onClick={handleDownloadPDF}
                  type="button"
                  disabled={isGeneratingPDF}
                >
                  {isGeneratingPDF ? (
                    <>
                      <Spinner /> Downloading PDF...
                    </>
                  ) : (
                    <>Download PDF</>
                  )}
                </button>
              </div>
            )}
            {error && <p className="text-red-500 mt-4 text-center">{error}</p>}
          </div>
        </div>
        {/* RIGHT: STICKY FORM */}
        <div className="relative w-full lg:w-[400px] mt-20 lg:mt-0">
          <div className="sticky top-32">
            <div className="bg-[var(--glass-surface)] backdrop-blur-[25px] border-[var(--glass-border)] p-6 md:p-12 shadow-[0_30px_60px_rgba(0,0,0,0.05)] clip-path-[polygon(10%_0,100%_0,100%_100%,0%_100%,0_5%)]">
              <h3 className="font-teko text-3xl text-text-obsidian mb-2">
                Mortgage Broker
              </h3>
              <p className="font-manrope text-sm text-text-slate leading-relaxed mb-8">
                Rates are changing daily. Speak to our in-house brokerage team
                for exclusive Buy-to-Let products and portfolio refinancing.
              </p>
              <form className="grid gap-5" onSubmit={handleContactSubmit}>
                {contactSuccess && (
                  <p className="font-manrope text-sm text-green-700 bg-green-50 p-4 border border-solid border-green-200">
                    Thank you — your enquiry has been sent.
                  </p>
                )}
                {contactError && (
                  <p className="font-manrope text-sm text-red-700 bg-red-50 p-4 border border-solid border-red-200">
                    {contactError}
                  </p>
                )}
                <input
                  type="text"
                  className="w-full p-4 bg-bg-white border border-solid border-[rgba(0,0,0,0.1)] font-manrope transition-all duration-300 clip-path-[polygon(0_0,100%_0,100%_85%,98%_100%,0_100%)] focus:outline-none focus:border-brand-yellow"
                  placeholder="Full Name"
                  id="mortgageBrokerFullNameInput"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  required
                  disabled={contactSubmitting}
                />
                <input
                  type="email"
                  className="w-full p-4 bg-bg-white border border-solid border-[rgba(0,0,0,0.1)] font-manrope transition-all duration-300 clip-path-[polygon(0_0,100%_0,100%_85%,98%_100%,0_100%)] focus:outline-none focus:border-brand-yellow"
                  placeholder="Email Address"
                  id="mortgageBrokerEmailAddressInput"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  required
                  disabled={contactSubmitting}
                />
                <input
                  type="tel"
                  className="w-full p-4 bg-bg-white border border-solid border-[rgba(0,0,0,0.1)] font-manrope transition-all duration-300 clip-path-[polygon(0_0,100%_0,100%_85%,98%_100%,0_100%)] focus:outline-none focus:border-brand-yellow"
                  placeholder="Phone Number"
                  id="mortgageBrokerPhoneNumberInput"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  disabled={contactSubmitting}
                />
                <textarea
                  className="w-full p-4 bg-bg-white border border-solid border-[rgba(0,0,0,0.1)] font-manrope transition-all duration-300 clip-path-[polygon(0_0,100%_0,100%_85%,98%_100%,0_100%)] focus:outline-none focus:border-brand-yellow"
                  rows={3}
                  placeholder="I'm looking to remortgage..."
                  id="mortgageBrokerMessageInput"
                  value={contactMessage}
                  onChange={(e) => setContactMessage(e.target.value)}
                  required
                  disabled={contactSubmitting}
                ></textarea>
                <button
                  type="submit"
                  className="w-full p-4 mt-4 bg-text-obsidian text-white border-none font-teko text-xl uppercase cursor-pointer transition-all duration-300 clip-path-[polygon(0_0,100%_0,100%_80%,95%_100%,0_100%)] hover:bg-brand-yellow hover:text-black disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={contactSubmitting}
                >
                  {contactSubmitting ? "Sending…" : "Request Quote"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* INFO CONTENT */}
      <section className="max-w-[1440px] mx-auto px-[5%] py-32 border-t border-t-solid border-t-[rgba(0,0,0,0.05)] sm:mt-16">
        <h3 className="font-teko text-3xl mb-6 border-l-4 border-l-solid border-l-brand-yellow pl-4">
          Buy-to-Let Criteria
        </h3>
        <p className="font-manrope text-base leading-relaxed text-text-slate mb-6 max-w-[800px]">
          Lenders typically require a rental income of at least 125% to 145% of
          the mortgage interest payments (calculated at a stress-test rate,
          often 5.5%). This is known as the Interest Coverage Ratio (ICR).
        </p>

        <h3 className="font-teko text-3xl mb-6 border-l-4 border-l-solid border-l-brand-yellow pl-4">
          Loan to Value (LTV)
        </h3>
        <p className="font-manrope text-base leading-relaxed text-text-slate mb-6 max-w-[800px]">
          Most BTL mortgages require a minimum deposit of 25% (75% LTV), though
          better rates are often available at 60% LTV. Higher leverage increases
          your Return on Capital Employed (ROCE) but exposes you to greater
          market risk.
        </p>
      </section>
    </>
  );
}
